import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axiosInstant from "../../../api";
import { getProductCategories } from "../../../api/endpoints";

interface ProductCategory {
  id: number;
  name: string;
  parent_id: number | null;
  external_id?: string | null;
}

interface CategoryOption {
  id: number;
  label: string; // full path, e.g. "Electronics › Mobile Devices › Smartphones"
}

interface Props {
  /** Selected product_category id(s). A number (single) or number[] (multiple). */
  value?: number | number[] | null;
  /** Formik setFieldValue. */
  setFieldValue: (field: string, value: any) => void;
  /** Field name written into the form. */
  name?: string;
  label?: string;
  className?: string;
  /** When true, allow selecting several categories; writes an id array. */
  multiple?: boolean;
}

/**
 * Searchable product-category picker. Fetches the flat category list once and
 * presents only leaf categories (no children), each labelled with its full
 * parent path so duplicate leaf names (e.g. "Audio" under Electronics vs Office)
 * are disambiguated. Supports single-select (writes an id) or multi-select
 * (writes an id array) via the `multiple` prop.
 */
export function FieldProductCategorySelect({
  value,
  setFieldValue,
  name = "product_category_id",
  label = "Product Category",
  className,
  multiple = false,
}: Props) {
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  useEffect(() => {
    let cancelled = false;
    axiosInstant
      .get(getProductCategories())
      .then((res) => {
        if (cancelled) return;
        // Endpoint may return the rows directly or wrapped — handle both.
        const rows: ProductCategory[] = res.data?.rows || res.data || [];
        setCategories(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Build leaf options with full-path labels.
  const options = useMemo<CategoryOption[]>(() => {
    if (categories.length === 0) return [];
    const byId = new Map<number, ProductCategory>();
    const hasChild = new Set<number>();
    categories.forEach((c) => {
      byId.set(c.id, c);
      if (c.parent_id != null) hasChild.add(c.parent_id);
    });

    const pathLabel = (c: ProductCategory): string => {
      const segs: string[] = [];
      let cur: ProductCategory | undefined = c;
      let depth = 0;
      while (cur && depth < 8) {
        segs.unshift(cur.name);
        cur = cur.parent_id != null ? byId.get(cur.parent_id) : undefined;
        depth += 1;
      }
      return segs.join(" › ");
    };

    return categories
      .filter((c) => !hasChild.has(c.id)) // leaves only
      .filter((c) => c.name !== "Uncategorized")
      .map((c) => ({ id: c.id, label: pathLabel(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [categories]);

  const selectedSingle = useMemo(
    () => options.find((o) => o.id === value) || null,
    [options, value]
  );

  const selectedMulti = useMemo(() => {
    const ids = Array.isArray(value) ? value : value != null ? [value] : [];
    return options.filter((o) => ids.includes(o.id));
  }, [options, value]);

  if (multiple) {
    return (
      <Autocomplete
        multiple
        options={options}
        value={selectedMulti}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(_e, vals) =>
          setFieldValue(name, (vals as CategoryOption[]).map((v) => v.id))
        }
        className={className}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search categories…"
            size="small"
          />
        )}
      />
    );
  }

  return (
    <Autocomplete
      options={options}
      value={selectedSingle}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, val) => setFieldValue(name, val ? val.id : null)}
      className={className}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search categories…"
          size="small"
        />
      )}
    />
  );
}

export default FieldProductCategorySelect;
